import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './SeasonalCollections.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SeasonalCollections = ({ sessionToken, onGenerate }) => {
  const [templates, setTemplates] = useState([]);
  const [userAlbums, setUserAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [photoSelectionMode, setPhotoSelectionMode] = useState('album'); // 'album', 'camera', 'upload'
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [uploadedPhoto, setUploadedPhoto] = useState(null);

  useEffect(() => {
    loadTemplates();
    loadUserAlbums();
  }, []);

  const loadTemplates = async () => {
    try {
      const response = await axios.get(`${API}/seasonal-templates`);
      if (response.data.success) {
        // Add custom template at the end
        const customTemplate = {
          id: 'custom-collection',
          name: 'Custom Collection',
          season: 'custom',
          description: 'Create your own personalized collection',
          icon: '🎯',
          search_keywords: [],
          is_custom: true
        };
        setTemplates([...response.data.templates, customTemplate]);
      }
    } catch (error) {
      console.error('Error loading templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUserAlbums = async () => {
    try {
      const response = await axios.get(`${API}/albums`, {
        headers: {
          Authorization: `Bearer ${sessionToken}`
        }
      });
      
      if (response.data.success) {
        // Show all albums with at least one photo
        const albumsWithPhotos = response.data.albums.filter(a => a.photos && a.photos.length > 0);
        setUserAlbums(albumsWithPhotos);
      }
    } catch (error) {
      console.error('Error loading albums:', error);
    }
  };

  const handleTemplateClick = (template) => {
    // Don't require albums anymore - allow direct photo capture/upload
    setSelectedTemplate(template);
    setShowGenerateModal(true);
    setError('');
    setPhotoSelectionMode('album'); // Default to album selection
    setCapturedPhoto(null);
    setUploadedPhoto(null);
  };

  const handleCameraCapture = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      setCapturedPhoto(reader.result);
      setPhotoSelectionMode('camera');
      setError('');
    };
    reader.readAsDataURL(file);
  };

  const handlePhotoUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file (JPG, PNG, etc.)');
      return;
    }

    // Validate file size (max 15MB)
    if (file.size > 15 * 1024 * 1024) {
      setError('Image file size too large. Maximum 15MB allowed.');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setUploadedPhoto(reader.result);
      setPhotoSelectionMode('upload');
      setError('');
    };
    reader.readAsDataURL(file);
  };

  const handleGenerate = async (albumId, photoId, backgroundStyle, directPhoto = null) => {
    try {
      setGenerating(true);
      setError('');

      const payload = {
        template_id: selectedTemplate.id,
        background_style: backgroundStyle || 'studio',
        generate_3d: false
      };

      // If direct photo is provided (camera/upload), use it
      if (directPhoto) {
        payload.photo_base64 = directPhoto;
      } else {
        // Otherwise use album and photo ID
        payload.album_id = albumId;
        payload.photo_id = photoId;
      }

      const response = await axios.post(
        `${API}/seasonal-albums/generate`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${sessionToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        setShowGenerateModal(false);
        setSelectedTemplate(null);
        
        // Show success message and redirect to gallery
        alert(`✅ ${response.data.seasonal_album.message}\n\n${response.data.seasonal_album.images.length} images generated!\n\nRedirecting to Seasonal Gallery...`);
        
        // Redirect to seasonal gallery
        setTimeout(() => {
          window.location.href = '/seasonal-gallery';
        }, 1000);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to generate seasonal album';
      setError(errorMsg);
      alert('❌ ' + errorMsg);
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return <div className="seasonal-loading">Loading seasonal collections...</div>;
  }

  return (
    <div className="seasonal-collections">
      <div className="seasonal-header">
        <h2>🌟 Discover Seasonal Collections</h2>
        <p>AI-curated outfits for every season and occasion</p>
      </div>

      {error && !showGenerateModal && (
        <div className="seasonal-error">
          {error}
        </div>
      )}

      <div className="templates-grid">
        {templates.map((template) => (
          <div
            key={template.id}
            className="template-card"
            onClick={() => handleTemplateClick(template)}
          >
            <div className="template-icon">{template.icon}</div>
            <h3>{template.name}</h3>
            <p>{template.description}</p>
            <div className="template-info">
              <span className="image-count">4 images</span>
              <span className={`credit-status ${userAlbums.length === 0 ? 'locked' : 'free'}`}>
                {userAlbums.length === 0 ? '🔒 Need Album' : '✨ First FREE!'}
              </span>
            </div>
            <button className="generate-btn">
              Generate Collection
            </button>
          </div>
        ))}
      </div>

      {showGenerateModal && selectedTemplate && (
        <GenerateModal
          template={selectedTemplate}
          albums={userAlbums}
          onClose={() => {
            setShowGenerateModal(false);
            setSelectedTemplate(null);
            setPhotoSelectionMode('album');
            setCapturedPhoto(null);
            setUploadedPhoto(null);
          }}
          onGenerate={handleGenerate}
          generating={generating}
          error={error}
          photoSelectionMode={photoSelectionMode}
          setPhotoSelectionMode={setPhotoSelectionMode}
          capturedPhoto={capturedPhoto}
          uploadedPhoto={uploadedPhoto}
          onCameraCapture={handleCameraCapture}
          onPhotoUpload={handlePhotoUpload}
        />
      )}
    </div>
  );
};

const GenerateModal = ({ 
  template, 
  albums, 
  onClose, 
  onGenerate, 
  generating, 
  error,
  photoSelectionMode,
  setPhotoSelectionMode,
  capturedPhoto,
  uploadedPhoto,
  onCameraCapture,
  onPhotoUpload
}) => {
  const [selectedAlbumId, setSelectedAlbumId] = useState(albums[0]?.id || '');
  const [selectedPhotoId, setSelectedPhotoId] = useState('');
  const [selectedAlbum, setSelectedAlbum] = useState(albums[0] || null);
  const [backgroundStyle, setBackgroundStyle] = useState('studio');
  
  // Custom collection states
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState('');
  const [brandProducts, setBrandProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [collectionName, setCollectionName] = useState('');
  const [loadingBrands, setLoadingBrands] = useState(false);

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => {
    const album = albums.find(a => a.id === selectedAlbumId);
    setSelectedAlbum(album || null);
    // Auto-select first photo when album changes
    if (album && album.photos && album.photos.length > 0) {
      setSelectedPhotoId(album.photos[0].id);
    } else {
      setSelectedPhotoId('');
    }
  }, [selectedAlbumId, albums]);

  // Load brands for custom collection
  useEffect(() => {
    if (template.is_custom) {
      loadMarketplaceBrands();
    }
  }, [template]);

  const loadMarketplaceBrands = async () => {
    try {
      setLoadingBrands(true);
      const response = await axios.get(`${API}/marketplace/brands?limit=20`);
      if (response.data.success) {
        setBrands(response.data.brands);
        if (response.data.brands.length > 0) {
          setSelectedBrand(response.data.brands[0].id);
        }
      }
    } catch (error) {
      console.error('Error loading brands:', error);
    } finally {
      setLoadingBrands(false);
    }
  };

  // Load products when brand changes
  useEffect(() => {
    if (selectedBrand && template.is_custom) {
      loadBrandProducts();
    }
  }, [selectedBrand]);

  const loadBrandProducts = async () => {
    try {
      const response = await axios.get(`${API}/marketplace/brands/${selectedBrand}/products?limit=20`);
      if (response.data.success) {
        setBrandProducts(response.data.products);
      }
    } catch (error) {
      console.error('Error loading products:', error);
    }
  };

  const toggleProductSelection = (productId) => {
    if (selectedProducts.includes(productId)) {
      setSelectedProducts(selectedProducts.filter(id => id !== productId));
    } else if (selectedProducts.length < 4) {
      setSelectedProducts([...selectedProducts, productId]);
    }
  };

  const handleCustomGenerate = () => {
    if (selectedProducts.length === 0) {
      alert('Please select at least 1 product');
      return;
    }
    if (!collectionName.trim()) {
      alert('Please name your collection');
      return;
    }
    
    // Call custom generation with brand products
    alert('Custom collection generation coming soon! Selected: ' + selectedProducts.length + ' products');
  };

  // Render custom collection UI
  if (template.is_custom) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content generate-modal custom-modal" onClick={(e) => e.stopPropagation()}>
          <h2>🎯 Create Custom Collection</h2>
          <p className="modal-subtitle">Build your personalized collection from marketplace brands</p>

          <div className="form-group">
            <label>Collection Name:</label>
            <input
              type="text"
              value={collectionName}
              onChange={(e) => setCollectionName(e.target.value)}
              placeholder="e.g., Summer Essentials, Office Wear..."
              disabled={generating}
            />
          </div>

          <div className="form-group">
            <label>Select Model Album:</label>
            <select
              value={selectedAlbumId}
              onChange={(e) => setSelectedAlbumId(e.target.value)}
              disabled={generating}
            >
              {albums.map((album) => (
                <option key={album.id} value={album.id}>
                  {album.name} ({album.category}) - {album.photos?.length || 0} photos
                </option>
              ))}
            </select>
          </div>

          {selectedAlbum && selectedAlbum.photos && selectedAlbum.photos.length > 0 && (
            <div className="form-group">
              <label>Select Model Photo:</label>
              <div className="photo-grid">
                {selectedAlbum.photos.map((photo) => (
                  <div
                    key={photo.id}
                    className={`photo-option ${selectedPhotoId === photo.id ? 'selected' : ''}`}
                    onClick={() => setSelectedPhotoId(photo.id)}
                  >
                    <img
                      src={photo.image_base64.startsWith('data:') ? photo.image_base64 : `data:image/jpeg;base64,${photo.image_base64}`}
                      alt={photo.position || 'Model photo'}
                    />
                    <div className="photo-label">{photo.position || 'Photo'}</div>
                    {selectedPhotoId === photo.id && <div className="selected-badge">✓</div>}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="form-group">
            <label>Select Brand:</label>
            {loadingBrands ? (
              <p>Loading brands...</p>
            ) : (
              <select
                value={selectedBrand}
                onChange={(e) => setSelectedBrand(e.target.value)}
                disabled={generating}
              >
                {brands.map((brand) => (
                  <option key={brand.id} value={brand.id}>
                    {brand.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          {selectedBrand && (
            <div className="form-group">
              <label>Select Products (up to 4):</label>
              {brandProducts.length === 0 ? (
                <div className="empty-products-message">
                  <p>📭 No products available from this brand yet.</p>
                  <p className="helper-text">This brand hasn't added any products to their catalog. Try selecting a different brand.</p>
                </div>
              ) : (
                <>
                  <div className="products-grid">
                    {brandProducts.map((product) => (
                      <div
                        key={product.id}
                        className={`product-option ${selectedProducts.includes(product.id) ? 'selected' : ''}`}
                        onClick={() => toggleProductSelection(product.id)}
                      >
                        {product.picture_link ? (
                          <img 
                            src={product.picture_link} 
                            alt={product.product_name}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div className="product-fallback" style={{display: 'none'}}>
                          📷
                        </div>
                        <div className="product-name">{product.product_name}</div>
                        <div className="product-price">${product.price}</div>
                        {selectedProducts.includes(product.id) && <div className="selected-badge">✓</div>}
                      </div>
                    ))}
                  </div>
                  <p className="helper-text">
                    Selected: {selectedProducts.length}/4 products
                  </p>
                </>
              )}
            </div>
          )}

          <div className="form-group">
            <label>Background Style ✨</label>
            <select
              value={backgroundStyle}
              onChange={(e) => setBackgroundStyle(e.target.value)}
              disabled={generating}
            >
              <option value="studio">🎬 Professional Studio</option>
              <option value="urban">🏙️ Urban Street</option>
              <option value="lifestyle">🏡 Lifestyle Casual</option>
              <option value="minimal">⚪ Minimal Elegant</option>
              <option value="luxury">💎 Luxury Boutique</option>
            </select>
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="modal-actions">
            <button
              className="btn-secondary"
              onClick={onClose}
              disabled={generating}
            >
              Cancel
            </button>
            <button
              className="btn-primary"
              onClick={handleCustomGenerate}
              disabled={generating || !selectedAlbumId || !selectedPhotoId || selectedProducts.length === 0 || !collectionName.trim()}
            >
              {generating ? 'Generating...' : 'Create Collection'}
            </button>
          </div>

          <div className="generation-note">
            <strong>Note:</strong> Custom collections let you choose specific products from marketplace brands.
          </div>
        </div>
      </div>
    );
  }

  // Render standard seasonal collection UI
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content generate-modal" onClick={(e) => e.stopPropagation()}>
        <h2>Generate {template.name}</h2>
        <p className="modal-subtitle">{template.icon} {template.description}</p>

        <div className="generation-info">
          <div className="info-item">
            <span className="info-label">Images:</span>
            <span className="info-value">4 try-on images</span>
          </div>
          <div className="info-item">
            <span className="info-label">Cost:</span>
            <span className="info-value">First seasonal album FREE, then 4 credits</span>
          </div>
        </div>

        <div className="form-group">
          <label>Choose Photo Source:</label>
          <div className="photo-source-selector">
            <button
              className={`source-btn ${photoSelectionMode === 'album' ? 'active' : ''}`}
              onClick={() => setPhotoSelectionMode('album')}
              disabled={generating}
              type="button"
            >
              📁 My Albums
            </button>
            <button
              className={`source-btn ${photoSelectionMode === 'camera' ? 'active' : ''}`}
              onClick={() => document.getElementById('camera-input').click()}
              disabled={generating}
              type="button"
            >
              📷 Take Photo
            </button>
            <button
              className={`source-btn ${photoSelectionMode === 'upload' ? 'active' : ''}`}
              onClick={() => document.getElementById('upload-input').click()}
              disabled={generating}
              type="button"
            >
              📤 Upload Photo
            </button>
          </div>
          <input
            id="camera-input"
            type="file"
            accept="image/*"
            capture="user"
            onChange={onCameraCapture}
            style={{ display: 'none' }}
          />
          <input
            id="upload-input"
            type="file"
            accept="image/*"
            onChange={onPhotoUpload}
            style={{ display: 'none' }}
          />
        </div>

        {photoSelectionMode === 'album' && albums.length > 0 && (
          <>
            <div className="form-group">
              <label>Select Model Album:</label>
              <select
                value={selectedAlbumId}
                onChange={(e) => setSelectedAlbumId(e.target.value)}
                disabled={generating}
              >
                {albums.map((album) => (
                  <option key={album.id} value={album.id}>
                    {album.name} ({album.category}) - {album.photos?.length || 0} photos
                  </option>
                ))}
              </select>
            </div>

            {selectedAlbum && selectedAlbum.photos && selectedAlbum.photos.length > 0 && (
              <div className="form-group">
                <label>Select Model Photo to Use:</label>
                <div className="photo-grid">
                  {selectedAlbum.photos.map((photo) => (
                    <div
                      key={photo.id}
                      className={`photo-option ${selectedPhotoId === photo.id ? 'selected' : ''}`}
                      onClick={() => setSelectedPhotoId(photo.id)}
                    >
                      <img
                        src={photo.image_base64.startsWith('data:') ? photo.image_base64 : `data:image/jpeg;base64,${photo.image_base64}`}
                        alt={photo.position || 'Model photo'}
                      />
                      <div className="photo-label">{photo.position || 'Photo'}</div>
                      {selectedPhotoId === photo.id && <div className="selected-badge">✓</div>}
                    </div>
                  ))}
                </div>
                <p className="helper-text">
                  This photo will be used for all virtual try-on images in this collection
                </p>
              </div>
            )}
          </>
        )}

        {photoSelectionMode === 'album' && albums.length === 0 && (
          <div className="no-albums-message">
            <p>📁 No albums yet. Choose "Take Photo" or "Upload Photo" to get started!</p>
          </div>
        )}

        {photoSelectionMode === 'camera' && capturedPhoto && (
          <div className="form-group">
            <label>Captured Photo:</label>
            <div className="preview-photo">
              <img src={capturedPhoto} alt="Captured" />
              <button
                className="retake-btn"
                onClick={() => document.getElementById('camera-input').click()}
                disabled={generating}
                type="button"
              >
                📷 Retake
              </button>
            </div>
          </div>
        )}

        {photoSelectionMode === 'upload' && uploadedPhoto && (
          <div className="form-group">
            <label>Uploaded Photo:</label>
            <div className="preview-photo">
              <img src={uploadedPhoto} alt="Uploaded" />
              <button
                className="retake-btn"
                onClick={() => document.getElementById('upload-input').click()}
                disabled={generating}
                type="button"
              >
                📤 Change Photo
              </button>
            </div>
          </div>
        )}

        <div className="form-group">
          <label>Background Style ✨</label>
          <select
            value={backgroundStyle}
            onChange={(e) => setBackgroundStyle(e.target.value)}
            disabled={generating}
            className="background-selector"
          >
            <option value="studio">🎬 Professional Studio</option>
            <option value="urban">🏙️ Urban Street</option>
            <option value="lifestyle">🏡 Lifestyle Casual</option>
            <option value="minimal">⚪ Minimal Elegant</option>
            <option value="luxury">💎 Luxury Boutique</option>
          </select>
          <p className="helper-text">
            AI-generated studio background for eye-catching product photos
          </p>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="modal-actions">
          <button
            className="btn-secondary"
            onClick={onClose}
            disabled={generating}
          >
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={() => {
              if (photoSelectionMode === 'album') {
                onGenerate(selectedAlbumId, selectedPhotoId, backgroundStyle);
              } else if (photoSelectionMode === 'camera' && capturedPhoto) {
                onGenerate(null, null, backgroundStyle, capturedPhoto);
              } else if (photoSelectionMode === 'upload' && uploadedPhoto) {
                onGenerate(null, null, backgroundStyle, uploadedPhoto);
              }
            }}
            disabled={
              generating || 
              (photoSelectionMode === 'album' && (!selectedAlbumId || !selectedPhotoId)) ||
              (photoSelectionMode === 'camera' && !capturedPhoto) ||
              (photoSelectionMode === 'upload' && !uploadedPhoto)
            }
          >
            {generating ? 'Generating...' : 'Generate Collection'}
          </button>
        </div>

        <div className="generation-note">
          <strong>Note:</strong> Generation may take 30-60 seconds as we search for seasonal items and create your personalized try-on images with studio backgrounds.
        </div>
      </div>
    </div>
  );
};

export default SeasonalCollections;
